//ThreeDemo.vue /* eslint-disable no-undef */
<template>
	<div id="wrapper">
		<div id="container" @mousedown="onMouseDown" @mouseup="onMouseUp"></div>
	</div>

	<!-- Drawing Tool -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<div id="container-draw"></div>

	<div id="input-bar">
		<InputBarDemo
			@updateMaterial="materialUpdate"
			@updateBuildingHeight="buildingHeightUpdate"
			@calculateClicked="calculate"
			@clearValuesClicked="clearValues"
			@updateLiveLoadValue="liveLoadValueUpdate"
			@updateDeadLoadValue="deadLoadValueUpdate"
			@updateFloorHeight="floorHeightUpdate"
			@updateXSpanValue="xSpanValueUpdate"
			@updateYSpanValue="ySpanValueUpdate"
			@updateBeamDistValue="beamDistValueUpdate"
			@updateStructuralSystem="structSystemUpdate"
			@collapseInputBarClicked="collapseInputBar"
		/>
	</div>

	<div id="result-bar">
		<ResultBar :building-result="buildingResult" v-if="isResultBarShow" @saveVariantClicked="saveVariant" @visualizeGeometryClicked="visualizeGeometry" @compareVariantsClicked="compareVariants" @checkResultsClicked="checkResults" @collapseResultBarClicked="collapseResultBar" :key="componentKey" :rotateResult="rotateResult" />
	</div>

	<!-- Dashboard -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<div v-if="showDashboard" id="dashboard">
		<Dashboard @closeDashboardClicked="closeDashboard" />
	</div>

	<!-- Check Modal -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<div v-if="showResultModal" id="check-modal">
		<CheckModal @closeModalClicked="closeModal" :member-results="memberResults" :struct-system="structSystem" />
	</div>

	<!-- Loading Animation -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<div v-show="loading" id="wrapper-loader">
		<LoaderBG />
	</div>

	<!-- Command Line -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<div id="command-line" v-if="showCommandLine">
		{{ commandLineText }}
		<div>
			<img id="icon-cross" src="imgs/icons/icon-cross.svg" @click="showCommandLine = !showCommandLine" />
		</div>
	</div>

	<!-- Gradient Canvas -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<canvas id="gradient-canvas"></canvas>

	<!-- Test Button -->
	<!-- --------------------------------------------------------------------------------------------- -->
	<!-- <button id="tell-me" @click="onClickTestMe">
		Test
	</button> -->
</template>

<script>
//--------------------------------------------------------------------------------------------------------------
//												IMPORT
//--------------------------------------------------------------------------------------------------------------

//Import Three.js
import * as THREE from "three";
import { PointTextHelper } from "@jniac/three-point-text-helper";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { TransformControls } from "three/examples/jsm/controls/TransformControls";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
// import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";

import gsap from "gsap";

//Import project classes
import * as MyFunctions from "../MyFunctions.js";
import * as BuildingGeometry from "../classes/BuildingGeometry.js";
import * as PreDimension from "../classes/PreDimension.js";
import * as StructuralMember from "../classes/StructuralMember.js";
import * as BuildingResult from "../classes/BuildingResult.js";
import * as MemberViz from "../classes/MemberViz.js";
import * as Color from "../classes/Color.js";
import * as ProfilLibrary from "../classes/ProfilLibrary.js";
import * as Firebase from "../firebase.js";

//Import Vue.js components
import InputBarDemo from "./InputBarDemo.vue";
import ResultBar from "./ResultBar.vue";
import Dashboard from "./Dashboard.vue";
import CheckModal from "./CheckModal.vue";
import LoaderBG from "./LoaderBG.vue";

//Import files/data

THREE.Object3D.DefaultUp.set(0, 0, 1);

window.THREE = THREE;

//--------------------------------------------------------------------------------------------------------------
//												VARIABLES
//--------------------------------------------------------------------------------------------------------------

//For Three.js
let container, renderer, scene, camera, orbitControl, transformControl, composer;
scene = new THREE.Scene();
// let sceneContent;
var mainGrid;
var grid10m;
var axesHelper;

//This is where the drawn vertices of the buildings floor plan get stored
var buildingPts = [];
var selectedCorePts = [];
var corePts = [];

//Visualization variables
var coreMesh;
var coreMeshes = [];
var extrBuildingMesh = new THREE.Mesh();
var extrCoreMesh = new THREE.Mesh();
var extrCoreMeshes = [];
var floorSlabMesh = new THREE.Mesh();
var floorSlabMeshes = [];

//Drawing variables
var drawnLine;
var updatedLine;
var count = 0;
var mouse = new THREE.Vector3();
var pos = new THREE.Vector3();
const lineHelperObjects = [];
const onUpPosition = new THREE.Vector2();
const onDownPosition = new THREE.Vector2();
var coreLine = new THREE.Line();
var pointTextHelper = new PointTextHelper({ charMax: 10 });

//Global building parameters
var levelCount;
var actualFloorHeight;

//Global Arrays
var buildingOutlines = [];
var gridlinesX = [];
var gridlinesY = [];
var intersectedGridlinesX = [];
var intersectedGridlinesY = [];
var intersectedSecondaryBeams = [];
var coreLines = [];
var xSegments = [];
var ySegments = [];
var secondaryBeamSegments = [];
var xBeamsPosition = [];
var xBeams = [];
var yBeamsPosition = [];
var yBeams = [];
var secondaryBeamsPosition = [];
var secondaryBeams = [];
var edgeBeams = [];
var beams = [];
var columns = [];
var innerColumnPositions = [];
var innerColumns = [];
var outerColumnPositions = [];
var outerColumns = [];
var edgeColumnPositions = [];
var edgeColumns = [];
var floorSlabs = [];
var groundSlab;

var buildingResults = [];

var edgeBeamsTest = [];

export default {
	components: {
		InputBarDemo,
		ResultBar,
		CheckModal,
		LoaderBG,
		Dashboard,
	},
	data() {
		return {
			componentKey: 0,
			material: "Steel",
			buildingHeight: 25.2, //5.0
			liveLoadValue: 2.0, //1.5
			deadLoadValue: 1.5, //1.5
			floorHeight: 4.2, //3.0
			xSpanValue: 9, //5.5
			ySpanValue: 9, //5.5
			beamDistValue: 1.5, //1.0
			structSystem: "Plate",
			dropdownResult: "",
			commandLineText: "Hello from Command Line",
			isResultBarShow: true,
			inputBarHidden: true,
			resultBarHidden: true,
			rotateResult: false,
			showResultModal: false,
			showGeometryInput: false,
			showCommandLine: false,
			showDashboard: false,
			fileInput: "",
			importedBuildingPoints: [],
			loading: false,
			sketchBuilding: false,
			importGeometry: false,
			slabHeight: 0,

			memberResults: {
				slabs: "",
				beams: "",
				columns: "",
			},

			buildingResult: {
				material: "",
				system: "",
				actXSpan: 0,
				actYSpan: 0,
				load: 0,
				surfaceArea: 0,
				clearFloorHeight: 0,
				structCeilingHeight: 0,
				weight: 0,
				cost: 0,
				carbonData: {
					total: 0,
					slabs: 0,
					columns: 0,
					foundation: 0,
					core: 0,
				},
				materialRecyclingContent: {},
				materialEndOfLife: {},
			},
		};
	},
	methods: {
		init() {
			//Get Database Material
			// this.getDatabaseMaterial();

			//Make gradient color
			this.makeGradientColor();

			container = document.getElementById("container");

			// Camera
			camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.01, 10000000);
			camera.position.set(50, 70, 40);
			camera.lookAt(new THREE.Vector3());
			scene = new THREE.Scene();

			//Renderer
			renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
			renderer.xr.enabled = true;
			renderer.setSize(container.clientWidth, container.clientHeight);
			renderer.setPixelRatio(window.devicePixelRatio);
			// renderer.setClearColor(0xf2f2f2);
			renderer.setClearColor(0xd4d4d4, 0);
			container.appendChild(renderer.domElement);

			//Composer
			composer = new EffectComposer(renderer);
			composer.addPass(new RenderPass(scene, camera));

			//Lights
			//Spot light
			// const spotLight = new THREE.SpotLight( 0xffffff, 1.5 );
			// spotLight.position.set( 0, 1500, 200 );
			// spotLight.angle = Math.PI * 0.2;
			// spotLight.castShadow = false;
			// spotLight.shadow.camera.near = 200;
			// spotLight.shadow.camera.far = 2000;
			// spotLight.shadow.bias = - 0.000222;
			// spotLight.shadow.mapSize.width = 1024;
			// spotLight.shadow.mapSize.height = 1024;
			// scene.add( spotLight );

			// //Ambient light
			const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
			scene.add(ambientLight);
			// //Directional light
			const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
			directionalLight.position.set(40, 40, 20);
			scene.add(directionalLight);

			//Lights
			// const light = new THREE.SpotLight( 0xffffff, 1.5 );
			// light.position.set( 0, 1500, 200 );
			// light.angle = Math.PI * 0.2;
			// light.castShadow = true;
			// light.shadow.camera.near = 200;
			// light.shadow.camera.far = 2000;
			// light.shadow.bias = - 0.000222;
			// light.shadow.mapSize.width = 1024;
			// light.shadow.mapSize.height = 1024;
			// scene.add( light );

			//Controls
			orbitControl = new OrbitControls(camera, renderer.domElement);
			orbitControl.enableDamping = true;
			orbitControl.dampingFactor = 0.2;
			orbitControl.update();

			transformControl = new TransformControls(camera, renderer.domElement);
			scene.add(transformControl);
			transformControl.addEventListener("change", this.render);
			transformControl.dampingFactor = 0;
			transformControl.translationSnap = 1;
			transformControl.showZ = false;
			transformControl.setSize(0.5);

			transformControl.addEventListener("dragging-changed", function (event) {
				orbitControl.enabled = !event.value;
				console.log("lineHelperObjects");
				console.log(lineHelperObjects);

				var pts = [];

				for (let i = 0; i < lineHelperObjects.length; i++) {
					pts.push(lineHelperObjects[i].position);
				}

				//Update coordinate viz
				scene.remove(pointTextHelper);
				pointTextHelper = new PointTextHelper({ charMax: 10 });
				scene.add(pointTextHelper);

				var x = 0;
				var y = 0;

				for (let i = 0; i < pts.length; i++) {
					x = x + pts[i].x;
					y = y + pts[i].y;
				}

				const midPt = new THREE.Vector3(x / pts.length, y / pts.length, 0);

				for (let i = 0; i < pts.length; i++) {
					var vect = new THREE.Vector3(pts[i].x - midPt.x, pts[i].y - midPt.y, 0);
					var norm = vect.normalize();
					pointTextHelper.display({ text: pts[i].x.toString() + "/" + pts[i].y.toString(), color: "black", size: 3, position: new THREE.Vector3(pts[i].x + norm.x, pts[i].y + norm.y, 0) });
				}

				buildingPts = pts;

				console.log(pts);
				console.log(buildingPts);
			});

			transformControl.addEventListener("dragging-changed", this.updateOutline);

			console.log("buildingPts");
			console.log(buildingPts);

			//Grid and Axes
			mainGrid = new THREE.GridHelper(100, 100);
			mainGrid.geometry.rotateX(Math.PI / 2);
			mainGrid.material.opacity = 0.25;
			mainGrid.material.transparent = true;

			grid10m = new THREE.GridHelper(100, 10);
			grid10m.geometry.rotateX(Math.PI / 2);
			grid10m.material.opacity = 0.45;
			grid10m.material.transparent = true;

			scene.add(mainGrid, grid10m);

			axesHelper = new THREE.AxesHelper(3); //x-Axis -> red, y-Axis -> green, z-Axis -> blue
			scene.add(axesHelper);

			//Event listeners
			window.addEventListener("pointermove", this.onPointerMove);
			window.addEventListener("pointerdown", this.onPointerDown);
			window.addEventListener("pointerup", this.onPointerUp);

			//Raycaster for drawing tool
			const raycaster = new THREE.Raycaster();
			var plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
			const clickMouse = new THREE.Vector2();

			//For Demo
			buildingPts = [new THREE.Vector3(-18, -18, 0), new THREE.Vector3(-18, 18, 0), new THREE.Vector3(18, 18, 0), new THREE.Vector3(18, -18, 0), new THREE.Vector3(-18, -18, 0)];

			this.buildingHeightUpdate(this.buildingHeight);
		},
		forceRerender() {
			this.componentKey += 1;
		},
		onClickTestMe() {
			console.log("test me clicked");

			// console.log(this.$store.state.databaseMaterial);

			// BuildingResult.printDataFromBuildingResults();

			// console.log('edgeBeamsTest:');
			// console.log(edgeBeamsTest);

			// for (let i = 0; i < edgeBeamsTest.length; i++) {
			// 	console.log(edgeBeamsTest[i]);
			// 	var beamMesh = MemberViz.visualizeBeam(edgeBeamsTest[i], edgeBeamsTest[i].shape, this.structSystem, 5, 0.2);
			// 	scene.add(beamMesh);
			// }
		},
		//--------------------------------------------------------------------------------------------------------------
		//From Input Bar
		//--------------------------------------------------------------------------------------------------------------
		materialUpdate(value) {
			this.material = value;
			console.log("Material changed to: " + this.material);
		},

		buildingHeightUpdate(value) {
			console.log(value);
			this.buildingHeight = value;

			console.log(buildingPts);

			//Remove building and core outline plus coord viz
			scene.remove(drawnLine);
			for (let i = 0; i < coreLines.length; i++) {
				scene.remove(coreLines[i]);
			}
			scene.remove(pointTextHelper);

			for (let i = 0; i < lineHelperObjects.length; i++) {
				scene.remove(lineHelperObjects[i]);
			}

			//Extrusion
			//---------------------------------------------------
			//Remove "old" extrusions
			scene.remove(extrBuildingMesh);

			for (let i = 0; i < extrCoreMeshes.length; i++) {
				scene.remove(extrCoreMeshes[i]);
			}

			const extrudeSettingsBuilding = {
				steps: 2,
				depth: this.buildingHeight,
				bevelEnabled: false,
				bevelThickness: 1,
				bevelSize: 1,
				bevelOffset: 0,
				bevelSegments: 1,
			};

			const extrudeSettingsCore = {
				steps: 2,
				depth: this.buildingHeight,
				bevelEnabled: false,
				bevelThickness: 1,
				bevelSize: 1,
				bevelOffset: 0,
				bevelSegments: 1,
			};

			//Parameters for extrude Building
			const extrBuildingGeometry = new THREE.ExtrudeGeometry(new THREE.Shape(buildingPts), extrudeSettingsBuilding);
			const extrBuildingMaterial = new THREE.MeshLambertMaterial({ color: Color.MaterialColor.buildingMesh, side: THREE.DoubleSide });
			extrBuildingMaterial.opacity = 0.3;
			extrBuildingMaterial.transparent = true;
			extrBuildingMaterial.depthWrite = false;
			// extrBuildingMaterial.wireframe = true;

			extrBuildingMesh = new THREE.Mesh(extrBuildingGeometry, extrBuildingMaterial);
			scene.add(extrBuildingMesh);

			//Parameters for extrude Cores
			// const extrCoreMaterial = new THREE.MeshBasicMaterial( { color: 0xBDB76B, side: THREE.DoubleSide} );
			const extrCoreMaterial = new THREE.MeshLambertMaterial({ color: Color.MaterialColor.core, side: THREE.DoubleSide });
			extrCoreMaterial.opacity = 0.2;
			extrCoreMaterial.transparent = true;

			for (let i = 0; i < corePts.length; i++) {
				var extrCoreGeometry = new THREE.ExtrudeGeometry(new THREE.Shape(corePts[i]), extrudeSettingsCore);
				extrCoreMesh = new THREE.Mesh(extrCoreGeometry, extrCoreMaterial);
				extrCoreMeshes.push(extrCoreMesh);

				scene.add(extrCoreMesh);
				scene.add(extrCoreMesh);
			}

			console.log("extrCoreMaterial");
			console.log(extrCoreMaterial);
		},
		liveLoadValueUpdate(value) {
			console.log(value);
			this.liveLoadValue = value;
		},
		deadLoadValueUpdate(value) {
			console.log(value);
			this.deadLoadValue = value;
		},
		floorHeightUpdate(value) {
			this.floorHeight = value;
			console.log(this.floorHeight);
		},
		xSpanValueUpdate(value) {
			this.xSpanValue = value;
			console.log(this.xSpanValue);
		},
		ySpanValueUpdate(value) {
			this.ySpanValue = value;
			console.log(this.ySpanValue);
		},
		beamDistValueUpdate(value) {
			this.beamDistValue = value;
			console.log(this.beamDistValue);
		},
		structSystemUpdate(value) {
			this.structSystem = value;
			console.log("Structural system changed to: " + this.structSystem);
		},
		collapseInputBar() {
			console.log("colapse input");

			const vw = (coef) => window.innerWidth * (coef / 100);

			var component = document.getElementById("input-bar1");
			if (this.inputBarHidden) {
				gsap.to(component, { duration: 0.4, left: 0 });
			}
			if (!this.inputBarHidden) {
				gsap.to(component, { duration: 0.4, left: vw(-15.5) });
			}
			this.inputBarHidden = !this.inputBarHidden;
		},
		collapseResultBar() {
			console.log("colapse result");

			const vw = (coef) => window.innerWidth * (coef / 100);

			var component = document.getElementById("result-bar1");
			if (this.resultBarHidden) {
				gsap.to(component, { duration: 0.4, right: 0 });
			}
			if (!this.resultBarHidden) {
				gsap.to(component, { duration: 0.4, right: vw(-19.5) });
			}
			this.resultBarHidden = !this.resultBarHidden;
		},
		//--------------------------------------------------------------------------------------------------------------
		//							MAIN METHOD
		//--------------------------------------------------------------------------------------------------------------
		calculate() {
			this.$store.state.columnLoadExceeded = false;

			this.showCommandLine = false;

			var warningExValues = this.catchExaggerateValues();

			this.showCommandLine = false;

			buildingPts = [new THREE.Vector3(-18, -18, 0), new THREE.Vector3(-18, 18, 0), new THREE.Vector3(18, 18, 0), new THREE.Vector3(18, -18, 0), new THREE.Vector3(-18, -18, 0)];

			if (this.material && this.structSystem && buildingPts.length > 0 && warningExValues == "No warning") {
				//Clear values from previous calculations
				// this.clearAll();
				this.clearValues();

				//Remove cores
				for (let i = 0; i < extrCoreMeshes.length; i++) {
					scene.remove(extrCoreMeshes[i]);
				}

				//Exception handling
				if (!this.material) {
					alert("You did not select a material!");
					location.reload();
				}
				if (!this.structSystem) {
					alert("You did not select a structural system!");
					location.reload();
				}

				//Only for quick tests when not wanting to draw -> see demo view
				//Building
				// buildingPts = [new THREE.Vector3(0, 0, 0),    new THREE.Vector3(0, 60, 0),  new THREE.Vector3(40, 60, 0), new THREE.Vector3(40, 40, 0),
				// 				new THREE.Vector3(20, 40, 0), new THREE.Vector3(20, 20, 0), new THREE.Vector3(40, 20, 0), new THREE.Vector3(40, 0, 0),
				// 				new THREE.Vector3(0, 0, 0 ) ];

				// Core
				// var corePts1 = [];
				// var corePts2 = [];
				// corePts1 = [new THREE.Vector3(14, 4, 0),  new THREE.Vector3(14, 8, 0),  new THREE.Vector3(8, 8, 0),  new THREE.Vector3(8, 4, 0) ];
				// corePts2 = [new THREE.Vector3(14, 54, 0), new THREE.Vector3(14, 58, 0), new THREE.Vector3(8, 58, 0), new THREE.Vector3(8, 54, 0)];
				// corePts.push(corePts1);
				// corePts.push(corePts2);

				console.log(corePts);

				//Show results
				this.isResultBarShow = false;
				this.isResultBarShow = true;
				this.resultBarHidden = true;
				this.collapseResultBar();

				if (!this.rotateResult) {
					this.rotateResult = true;
				}

				this.$store.state.showResultBar = true;

				//Calculate values
				//--------------------------------------------------------------------------------------------------------------
				levelCount = Math.floor(this.buildingHeight / this.floorHeight);
				actualFloorHeight = (this.buildingHeight / levelCount).toFixed(2);
				const buildingGeometry = new THREE.BufferGeometry().setFromPoints(buildingPts);
				var boundingLines = MyFunctions.getBoundingRectangle(buildingGeometry);
				var buildingLength = boundingLines[0].distance(); //X-Dimension
				var buildingWidth = boundingLines[2].distance(); //Y-Dimension
				var xBays = Math.floor(buildingLength / this.xSpanValue);
				var actualXSpan = (buildingLength / xBays).toFixed(2);
				var yBays = Math.floor(buildingWidth / this.ySpanValue);
				var actualYSpan = (buildingWidth / yBays).toFixed(2);
				var surfaceAreaPerStory = MyFunctions.getSurfaceArea(buildingPts, corePts);
				console.log("Surface Area Per Story: " + surfaceAreaPerStory);
				console.log("Stories: " + levelCount);
				console.log("Floor Height: " + actualFloorHeight);
				console.log("Building Length: " + buildingLength);
				console.log("Building Width: " + buildingWidth);
				console.log("X-Span: " + this.xSpanValue);
				console.log("Y-Span: " + this.ySpanValue);
				console.log("Act. X-Span: " + actualXSpan);
				console.log("Act. Y-Span: " + actualYSpan);
				console.log("X-Bays: " + xBays);
				console.log("Y-Bays: " + yBays);
				console.log("Beam Distance: " + this.beamDistValue);

				//Calculate total load
				// const deadLoad = 1.5; 		//kN/m2

				var totalLoad = this.deadLoadValue + this.liveLoadValue;

				//Remove the building envelope when the slabs are added to the scene
				scene.remove(extrBuildingMesh);

				//Extrude core according to the actual floor heights
				//--------------------------------------------------------------------------------------------------------------
				for (let i = 0; i < extrCoreMeshes.length; i++) {
					scene.remove(extrCoreMeshes[i]);
				}

				const extrCoreMaterial = new THREE.MeshBasicMaterial({ color: Color.MaterialColor.core, side: THREE.DoubleSide });
				extrCoreMaterial.opacity = 0.2;
				extrCoreMaterial.transparent = true;
				const extrudeSettingsCore = {
					steps: 2,
					depth: actualFloorHeight * levelCount,
					bevelEnabled: false,
					bevelThickness: 1,
					bevelSize: 1,
					bevelOffset: 0,
					bevelSegments: 1,
				};

				for (let i = 0; i < corePts.length; i++) {
					var extrCoreGeometry = new THREE.ExtrudeGeometry(new THREE.Shape(corePts[i]), extrudeSettingsCore);
					extrCoreMesh = new THREE.Mesh(extrCoreGeometry, extrCoreMaterial);
					extrCoreMeshes.push(extrCoreMesh);
					scene.add(extrCoreMesh);
				}

				console.log("extrCoreMaterial");
				console.log(extrCoreMaterial);

				//Slab Shape
				//--------------------------------------------------------------------------------------------------------------
				//Remove previously added slabs
				for (let i = 0; i < floorSlabMeshes.length; i++) {
					scene.remove(floorSlabMesh);
				}

				const floorSlabShape = new THREE.Shape(buildingPts);
				const meshSlabMaterial = new THREE.MeshBasicMaterial({ color: Color.MaterialColor.slabs, side: THREE.DoubleSide });
				meshSlabMaterial.opacity = 0.2;
				meshSlabMaterial.transparent = true;

				//Dimension of the slabs
				for (let i = 1; i <= levelCount; i++) {
					var slabPts = [];

					for (let j = 0; j < buildingPts.length; j++) {
						var slabPt = new THREE.Vector3(buildingPts[j].x, buildingPts[j].y, parseFloat(actualFloorHeight));
						slabPts.push(slabPt);
					}
					// console.log("slabPts");
					// console.log(slabPts)
					var slab = new StructuralMember.Slab(slabPts, this.material, this.structSystem, i, totalLoad, surfaceAreaPerStory);

					//Predimension slab
					var slabCrossSection = PreDimension.predimSlab(this.material, this.structSystem, actualXSpan, actualYSpan, this.beamDistValue, this.liveLoadValue, this.deadLoadValue);
					slab.height = slabCrossSection;
					//LCA of slab per m2
					slab.carbon = BuildingResult.calculateCarbonForSlab(this.material, slabCrossSection, this.structSystem);
					//Cost of slab per m2
					slab.cost = BuildingResult.calculateCostsForSlab(this.material, slabCrossSection, this.structSystem);
					//Weight per m2
					slab.weight = BuildingResult.calculateWeightForSlab(this.material, slabCrossSection, this.structSystem);

					floorSlabs.push(slab);

					console.log(slab);
				}

				//Get concrete plate Height if material == Concrete
				var concretePlateHeight = 0;
				if (this.material == "Concrete") {
					concretePlateHeight = slab.height;
					// console.log('concretePlateHeight ' + concretePlateHeight);
				}

				//Add slabweight to deadload

				var totalDeadLoad = this.deadLoadValue + floorSlabs[0].weight;
				console.log("Total dead load: " + totalDeadLoad + "kN/m2");

				//Beam distance
				if (this.material == "Timber" && this.structSystem == "Beam") {
					this.beamDistValue = 0.6;
				}
				if (this.material == "Hybrid" && this.structSystem == "Plate") {
					this.beamDistValue = floorSlabs[0].height.distance;
				}

				//Adding the slabs to the scene
				//--------------------------------------------------------------------------------------------------------------

				//CoreShape
				for (let i = 0; i < corePts.length; i++) {
					var coreHolePath = new THREE.Path(corePts[i]);
					floorSlabShape.holes.push(coreHolePath);
				}

				var floorSlabShapeGeometry = new THREE.ShapeBufferGeometry(floorSlabShape);
				floorSlabMesh = new THREE.Mesh(floorSlabShapeGeometry, meshSlabMaterial);
				// scene.add(floorSlabMesh);			//Ground slab
				//Floor slabs
				for (let i = 1; i <= levelCount; i++) {
					var floorSlab = floorSlabShapeGeometry.clone();
					floorSlab.translate(0, 0, i * actualFloorHeight);
					var floorSlabMesh = new THREE.Mesh(floorSlab, meshSlabMaterial);
					floorSlabMeshes.push(floorSlabMesh);
					scene.add(floorSlabMesh);
				}

				// //Building outline
				// buildingPts.push( new THREE.Vector3(3, 1, 0), new THREE.Vector3(6, 3, 0), new THREE.Vector3(4, 8, 0), new THREE.Vector3(1, 4, 0), new THREE.Vector3(3, 1, 0)  )
				// const buildingGeometry = new THREE.BufferGeometry().setFromPoints( buildingPts );
				// const myLine = new THREE.Line( buildingGeometry, new THREE.LineBasicMaterial( { color: 0x0000ff } ));
				// scene.add(myLine);

				// //Bounding rectangle
				// var boundingLines = MyFunctions.getBoundingRectangle(buildingGeometry);
				// for( let i = 0; i < boundingLines.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(boundingLines[i]));
				// }

				buildingOutlines = MyFunctions.closedPolylineFromPoints(buildingPts);
				// for( let i = 0; i < buildingOutlines.length; i++) {
				// 	scene.add(new THREE.Line( MyFunctions.lineToGeometry(buildingOutlines[i]), new THREE.LineBasicMaterial( { color: 0x000000 } )));
				// }
				console.log(buildingOutlines);

				// //Gridlines
				gridlinesX = BuildingGeometry.createGridLinesX(boundingLines, yBays);
				// for( let i = 0; i < gridlinesX.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(gridlinesX[i]));
				// }
				// console.log(gridlinesX);

				gridlinesY = BuildingGeometry.createGridLinesY(boundingLines, xBays);
				// for( let i = 0; i < gridlinesY.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(gridlinesY[i]));
				// }
				// console.log(gridlinesY);

				var gridlinesSecondaryBeams = BuildingGeometry.createGridLinesSecondaryBeams(boundingLines, xBays, yBays, actualXSpan, actualYSpan, this.beamDistValue);

				// for( let i = 0; i < gridlinesSecondaryBeams.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(gridlinesSecondaryBeams[i]));
				// }

				// //Intersection Gridlines - Building
				intersectedGridlinesX = BuildingGeometry.createIntersectedGridLinesX(gridlinesX, buildingOutlines);
				// for( let i = 0; i < intersectedGridlinesX.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(intersectedGridlinesX[i]));
				// }

				intersectedGridlinesY = BuildingGeometry.createIntersectedGridLinesX(gridlinesY, buildingOutlines);
				// for( let i = 0; i < intersectedGridlinesY.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(intersectedGridlinesY[i]));
				// }

				intersectedSecondaryBeams = BuildingGeometry.createIntersectedSecondaryBeams(gridlinesSecondaryBeams, buildingOutlines);
				// for( let i = 0; i < intersectedSecondaryBeams.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(intersectedSecondaryBeams[i]));
				// }

				// Core intersection
				//--------------------------------------------------------------------------------------------------------------
				coreLines = [];

				// console.log(corePts);
				// console.log("Core Pts");

				for (let i = 0; i < corePts.length; i++) {
					var coreLinesIteration = BuildingGeometry.createCoreLines(corePts[i]);
					coreLines = coreLines.concat(coreLinesIteration);
				}

				// for( let i = 0; i < coreLines.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(coreLines[i]));
				// }

				xSegments = BuildingGeometry.intersectXWithCores(intersectedGridlinesX, coreLines);
				// for( let i = 0; i < xSegments.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(xSegments[i]));
				// }

				ySegments = BuildingGeometry.intersectYWithCores(intersectedGridlinesY, coreLines);
				// for( let i = 0; i < ySegments.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(ySegments[i]));
				// }

				secondaryBeamSegments = BuildingGeometry.intersectSecondaryBeamsWithCores(intersectedSecondaryBeams, coreLines, actualXSpan, actualYSpan);
				// for( let i = 0; i < secondaryBeamSegments.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(secondaryBeamSegments[i]));
				// }

				//Beams
				//--------------------------------------------------------------------------------------------------------------
				//X-Beams
				xBeamsPosition = BuildingGeometry.getXBeamsPositions(xSegments, ySegments);
				// xBeams = BuildingGeometry.createBeams(xBeamsPosition, levelCount, actualFloorHeight);
				// for( let i = 0; i < xBeams.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(xBeams[i]));
				// }
				// // console.log(BuildingGeometry.createXBeams(xSegments, ySegments));

				// //Y-Beams
				yBeamsPosition = BuildingGeometry.getYBeamsPositions(ySegments, xSegments);
				// yBeams = BuildingGeometry.createBeams(yBeamsPosition, levelCount, actualFloorHeight);
				// for( let i = 0; i < yBeams.length; i++) {
				// 	scene.add(MyFunctions.lineToGeometry(yBeams[i]));
				// }
				// // console.log(BuildingGeometry.createYBeams(ySegments, xSegments));

				// //Secondary-Beams
				secondaryBeamsPosition = BuildingGeometry.getSecondaryBeamsPositions(secondaryBeamSegments, xSegments, ySegments, actualXSpan, actualYSpan);
				// secondaryBeams = BuildingGeometry.createBeams(secondaryBeamsPosition, levelCount, actualFloorHeight);

				//Merge all beams into one array
				beams = BuildingGeometry.createBeams(xBeamsPosition, yBeamsPosition, secondaryBeamsPosition, actualXSpan, actualYSpan, this.material, this.structSystem, levelCount, actualFloorHeight, this.liveLoadValue, totalDeadLoad, this.beamDistValue);
				// console.log("beams");
				// console.log(beams);

				//Add all beams to scene
				for (let i = 0; i < beams.length; i++) {
					scene.add(new THREE.Line(MyFunctions.lineToGeometry(beams[i]), new THREE.LineBasicMaterial({ color: Color.MaterialColor.beams })));
				}

				//Predimension of beams
				for (let i = 0; i < beams.length; i++) {
					beams[i].crossSection = PreDimension.predimBeam(beams[i], this.material, this.liveLoadValue, totalDeadLoad, beams[i].length, actualXSpan, actualYSpan, this.beamDistValue, this.structSystem, concretePlateHeight);
				}

				// //Edge-Beams
				var edgeBeamLines = MyFunctions.closedPolylineFromPoints(buildingPts);

				for (let i = 0; i < edgeBeamLines.length; i++) {
					if (edgeBeamLines[i].distance() < 0.001) {
						edgeBeamLines.splice(i);
					}
				}

				for (let i = 0; i < levelCount; i++) {
					for (let j = 0; j < edgeBeamLines.length; j++) {
						var edgeBeam = new StructuralMember.Beam(new THREE.Vector3(edgeBeamLines[j].start.x, edgeBeamLines[j].start.y, actualFloorHeight * (i + 1)), new THREE.Vector3(edgeBeamLines[j].end.x, edgeBeamLines[j].end.y, actualFloorHeight * (i + 1)), this.material, "Primary", levelCount, totalLoad / 2);

						edgeBeam.crossSection = PreDimension.predimBeam(edgeBeam, this.material, this.liveLoadValue / 2, totalDeadLoad / 2, Math.max(actualXSpan, actualYSpan), actualXSpan, actualYSpan, this.beamDistValue, this.structSystem, concretePlateHeight);

						edgeBeams.push(edgeBeam);
					}
				}

				console.log("edgeBeams");
				console.log(edgeBeams);

				edgeBeamsTest = edgeBeams;

				for (let i = 0; i < edgeBeams.length; i++) {
					scene.add(new THREE.Line(MyFunctions.lineToGeometry(edgeBeams[i]), new THREE.LineBasicMaterial({ color: Color.MaterialColor.beams })));
				}

				beams.concat(edgeBeams);

				console.log("Beams before carbon etc calc: ");
				console.log(beams);

				//Set performance indicators of all beams
				for (let i = 0; i < beams.length; i++) {
					beams[i].carbon = BuildingResult.calculateCarbonForBeam(this.material, this.structSystem, beams[i].crossSection) * beams[i].length;
					beams[i].cost = BuildingResult.calculateCostsForBeam(this.material, this.structSystem, beams[i].crossSection) * beams[i].length;
					beams[i].weight = BuildingResult.calculateWeightForBeam(this.material, this.structSystem, beams[i].crossSection) * beams[i].length;
				}

				console.log("Beams: ");
				console.log(beams);
				console.log("Columns: ");
				console.log(columns);

				//Column creation and predimension of columns
				//--------------------------------------------------------------------------------------------------------------
				// //Inner Columns
				innerColumnPositions = BuildingGeometry.getInnerColumnsPositions(xSegments, ySegments);
				// console.log(innerColumnPositions);
				// innerColumns = BuildingGeometry.createColumns(innerColumnPositions, levelCount, actualFloorHeight);

				console.log("Load before column dimension: ");
				console.log("liveLoad" + this.liveLoadValue);
				console.log("totalDeadLoad" + totalDeadLoad);

				innerColumns = BuildingGeometry.createColumns(innerColumnPositions, levelCount, actualFloorHeight, this.material, this.structSystem, this.liveLoadValue, totalDeadLoad, actualXSpan, actualYSpan, "inner");

				// console.log(innerColumns);

				for (let i = 0; i < innerColumns.length; i++) {
					scene.add(new THREE.Line(MyFunctions.lineToGeometry(innerColumns[i]), new THREE.LineBasicMaterial({ color: Color.MaterialColor.columns })));
				}

				// //Outer Columns
				outerColumnPositions = BuildingGeometry.getOuterColumnsPositions(intersectedGridlinesX, intersectedGridlinesY);
				// console.log(outerColumnPositions);
				outerColumns = BuildingGeometry.createColumns(outerColumnPositions, levelCount, actualFloorHeight, this.material, this.structSystem, this.liveLoadValue, totalDeadLoad, actualXSpan, actualYSpan, "outer");
				for (let i = 0; i < outerColumns.length; i++) {
					scene.add(new THREE.Line(MyFunctions.lineToGeometry(outerColumns[i]), new THREE.LineBasicMaterial({ color: Color.MaterialColor.columns })));
				}

				// //Edge columns
				edgeColumnPositions = BuildingGeometry.getEdgeColumnsPositions(buildingGeometry);
				edgeColumnPositions.pop();
				// console.log(edgeColumnPositions);
				edgeColumns = BuildingGeometry.createColumns(edgeColumnPositions, levelCount, actualFloorHeight, this.material, this.structSystem, this.liveLoadValue, totalDeadLoad, actualXSpan, actualYSpan, "edge");
				for (let i = 0; i < edgeColumns.length; i++) {
					scene.add(new THREE.Line(MyFunctions.lineToGeometry(edgeColumns[i]), new THREE.LineBasicMaterial({ color: Color.MaterialColor.columns })));
				}

				//Merge all columns
				columns = innerColumns.concat(outerColumns, edgeColumns);

				//Core calculation
				//--------------------------------------------------------------------------------------------------------------
				var cores = [];

				var coreVolume = 0;

				for (let i = 0; i < corePts.length; i++) {
					var coreCircumference = 0;
					var coreLines = BuildingGeometry.createCoreLines(corePts[i]);
					var core = new StructuralMember.Core(coreLines, this.buildingHeight);

					for (let j = 0; j < coreLines.length; j++) {
						var distance = coreLines[j].distance();
						coreCircumference = coreCircumference + distance;
					}

					core.circumference = coreCircumference;
					var coreCrossSection = PreDimension.predimCoreWalls();
					core.crossSection = coreCrossSection;
					core.carbon = BuildingResult.calculateCarbonForCore(coreCircumference, this.buildingHeight, coreCrossSection);
					core.cost = BuildingResult.calculateCostsForCore(coreCircumference, this.buildingHeight, coreCrossSection);
					core.weight = BuildingResult.calculateWeightForCore(coreCircumference, this.buildingHeight, coreCrossSection);

					cores.push(core);

					coreVolume = coreVolume + coreCircumference * this.buildingHeight * coreCrossSection;
				}

				console.log("Cores");
				console.log(cores);

				//Ground slab calculation
				//--------------------------------------------------------------------------------------------------------------
				groundSlab = new StructuralMember.GroundSlab(buildingPts);
				var groundSlabCrossSection = PreDimension.predimGroundSlab(this.buildingHeight, levelCount);
				groundSlab.crossSection = groundSlabCrossSection;
				groundSlab.carbon = BuildingResult.calculateCarbonForGroundSlab(groundSlabCrossSection);
				groundSlab.cost = BuildingResult.calculateCostsForGroundSlab(groundSlabCrossSection);
				groundSlab.weight = BuildingResult.calculateWeightForGroundSlab(groundSlabCrossSection);

				var groundSlabVolume = groundSlabCrossSection * surfaceAreaPerStory;

				console.log("Ground slab");
				console.log(groundSlab);

				//Capture performance indicators result

				//Surface Area
				this.buildingResult.surfaceArea = Math.round(surfaceAreaPerStory * levelCount);

				//Structural Ceiling Height
				this.slabHeight = BuildingGeometry.getSlabHeight(floorSlabs[0]);
				var structCeilingHeight = BuildingGeometry.getStructuralCeilingHeight(this.material, this.structSystem, floorSlabs[0], this.slabHeight, beams).toFixed(2);
				this.buildingResult.structCeilingHeight = structCeilingHeight;

				//Clear Floor Height
				this.buildingResult.clearFloorHeight = actualFloorHeight - structCeilingHeight / 2;

				this.buildingResult.actXSpan = actualXSpan;
				this.buildingResult.actYSpan = actualYSpan;

				//Recycling
				var materialContent = BuildingResult.calculateMaterialContent(this.material, this.structSystem, floorSlabs, columns, beams, coreVolume, groundSlabVolume, surfaceAreaPerStory);

				// console.log('Material Content');
				// console.log(materialContent);
				var materialRecyclingContent = BuildingResult.calculateMaterialRecyclingContent(materialContent);
				this.buildingResult.materialRecyclingContent = materialRecyclingContent;
				var materialEndOfLife = BuildingResult.calculateMaterialEndOfLife(materialContent);
				this.buildingResult.materialEndOfLife = materialEndOfLife;

				//Carbon data, cost and weight
				//Columns
				for (let i = 0; i < columns.length; i++) {
					this.buildingResult.carbonData.columns = this.buildingResult.carbonData.columns + columns[i].carbon;
					this.buildingResult.cost = this.buildingResult.cost + columns[i].cost;
					this.buildingResult.weight = this.buildingResult.weight + columns[i].weight;
				}
				//Beams
				for (let i = 0; i < beams.length; i++) {
					this.buildingResult.carbonData.slabs = this.buildingResult.carbonData.slabs + beams[i].carbon;
					this.buildingResult.cost = this.buildingResult.cost + beams[i].cost;
					this.buildingResult.weight = this.buildingResult.weight + beams[i].weight;
				}
				//Slabs
				for (let i = 0; i < floorSlabs.length; i++) {
					this.buildingResult.carbonData.slabs = this.buildingResult.carbonData.slabs + floorSlabs[i].carbon * surfaceAreaPerStory;
					this.buildingResult.cost = this.buildingResult.cost + floorSlabs[i].cost * surfaceAreaPerStory;
					this.buildingResult.weight = this.buildingResult.weight + floorSlabs[i].weight * surfaceAreaPerStory;
				}
				//Cores
				for (let i = 0; i < cores.length; i++) {
					this.buildingResult.carbonData.core = this.buildingResult.carbonData.core + cores[i].carbon;
					this.buildingResult.cost = this.buildingResult.cost + cores[i].cost;
					this.buildingResult.weight = this.buildingResult.weight + cores[i].weight;
				}
				//Ground Slab
				this.buildingResult.carbonData.foundation = this.buildingResult.carbonData.foundation + groundSlab.carbon * surfaceAreaPerStory;
				this.buildingResult.cost = this.buildingResult.cost + groundSlab.cost * surfaceAreaPerStory;
				this.buildingResult.weight = this.buildingResult.weight + groundSlab.weight * surfaceAreaPerStory;

				//Format results
				//--------------------------------------------------------------------------------------------------------------
				this.buildingResult.carbonData.slabs = Math.round(this.buildingResult.carbonData.slabs);
				this.buildingResult.carbonData.columns = Math.round(this.buildingResult.carbonData.columns);
				this.buildingResult.carbonData.core = Math.round(this.buildingResult.carbonData.core);
				this.buildingResult.carbonData.foundation = Math.round(this.buildingResult.carbonData.foundation);
				this.buildingResult.weight = Math.round(this.buildingResult.weight);
				this.buildingResult.cost = Math.round(this.buildingResult.cost);

				//Total carbon
				this.buildingResult.carbonData.total = this.buildingResult.carbonData.slabs + this.buildingResult.carbonData.columns + this.buildingResult.carbonData.core + this.buildingResult.carbonData.foundation;

				//Other parameters
				this.buildingResult.material = this.material;
				this.buildingResult.system = BuildingResult.getStructuralSystem(this.material, this.structSystem);
				this.buildingResult.grid = `${actualXSpan} m x ${actualYSpan} m`;
				this.buildingResult.load = this.liveLoadValue;

				console.log("Building Result:");
				console.log(this.buildingResult);

				//Logging in check modal
				this.memberResults.slabs = floorSlabs;
				this.memberResults.beams = beams;
				this.memberResults.columns = columns;

				// scene.remove(mainGrid);
				// scene.remove(grid10m);
				// scene.remove(axesHelper);

				//For Roche case study
				// this.createRoche1();

				//Rerender Result bar to update Carbon Chart
				this.forceRerender();
			}

			//Exception handling
			//--------------------------------------------------------------------------------------------------------------
			else if (buildingPts < 1) {
				alert("You did not select a building geometry!");
			} else if (!this.material && !this.structSystem) {
				alert("You did not select a material and a structural system!");
			} else if (!this.material) {
				alert("You did not select a material!");
			} else if (!this.structSystem) {
				alert("You did not select a structural system!");
			} else if (warningExValues != "No warning") {
				alert(warningExValues);
			}
		},
		clearValues() {
			this.showCommandLine = false;

			//Remove all added lines
			// for( var i = scene.children.length - 1; i >= 0; i--) {
			// 	var obj = scene.children[i];
			// 	scene.remove(obj);
			// }

			while (scene.children.length) {
				scene.remove(scene.children[0]);
			}

			//Lights
			// //Ambient light
			const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
			scene.add(ambientLight);
			// //Directional light
			const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
			directionalLight.position.set(40, 40, 20);
			scene.add(directionalLight);

			//Grid and Axes
			mainGrid = new THREE.GridHelper(100, 100);
			mainGrid.geometry.rotateX(Math.PI / 2);
			mainGrid.material.opacity = 0.25;
			mainGrid.material.transparent = true;

			grid10m = new THREE.GridHelper(100, 10);
			grid10m.geometry.rotateX(Math.PI / 2);
			grid10m.material.opacity = 0.45;
			grid10m.material.transparent = true;

			scene.add(mainGrid, grid10m);

			axesHelper = new THREE.AxesHelper(3); //x-Axis -> red, y-Axis -> green, z-Axis -> blue
			scene.add(axesHelper);

			//Empty all member arrays
			this.clearAll();

			//For Demo
			buildingPts = [new THREE.Vector3(-18, -18, 0), new THREE.Vector3(-18, 18, 0), new THREE.Vector3(18, 18, 0), new THREE.Vector3(18, -18, 0), new THREE.Vector3(-18, -18, 0)];

			this.buildingHeightUpdate(this.buildingHeight);
		},

		//--------------------------------------------------------------------------------------------------------------
		//From Result Bar
		//--------------------------------------------------------------------------------------------------------------
		saveVariant() {
			console.log("Save Variant clicked");

			this.showCommandLine = true;
			this.commandLineText = "Please request full version.";

			// this.showCommandLine = false;

			// //Increase counter
			// this.$store.commit('increaseCounter');

			// //Stop variant selection at 6 variants
			// if(this.$store.state.counter > 6) {
			// 	alert("You are only able to save up to 6 different variants!")
			// }

			// //Create a new BuildingResult object for the actual results
			// var definedStructSystem = BuildingResult.getStructuralSystem(this.material, this.structSystem);

			// // constructor(material, system, actXSpan, actYSpan, load, surfaceArea, clearFloorHeight, structCeilingHeight, weight, cost, carbonData)
			// var buildingResult = new BuildingResult.BuildingResult(this.material, definedStructSystem, this.buildingResult.actXSpan,
			// 				this.buildingResult.actYSpan, this.buildingResult.load, this.buildingResult.surfaceArea,
			// 				this.buildingResult.clearFloorHeight, this.buildingResult.structCeilingHeight, this.buildingResult.weight,
			// 				this.buildingResult.cost, this.buildingResult.carbonData.total);

			// console.log("Added buildingResult: " + buildingResult);

			// //Add the BuildingResult object to the  Vuex store buildingResults objects array
			// this.$store.commit('addResult', buildingResult);

			// //Logging results
			// console.log('this.$store.state.buildingResults from Three.vue');
			// console.log(this.$store.state.buildingResults);
		},
		compareVariants() {
			console.log("Compare Variants clicked");

			this.showCommandLine = true;
			this.commandLineText = "Please request full version.";

			// if(this.$store.state.counter < 1) {
			// 	alert("No saved variants to compare!")
			// }

			// this.showCommandLine = false;

			// this.showDashboard = true;

			// // this.$router.replace({ name: "Dashboard" });
		},
		visualizeGeometry() {
			console.log("visualizeGeometry clicked");

			if (this.buildingResult.cost != 0) {
				this.showCommandLine = false;

				//Change scene
				//Grid and Axes
				scene.remove(mainGrid, grid10m);
				scene.remove(axesHelper);

				while (scene.children.length) {
					scene.remove(scene.children[0]);
				}

				//Lights
				// //Ambient light
				const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
				scene.add(ambientLight);
				// //Directional light
				const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
				directionalLight.position.set(40, 40, 20);
				scene.add(directionalLight);

				//-------------------------------------------------------------------------------------------------------------------------
				//Member visualization
				//-------------------------------------------------------------------------------------------------------------------------

				//Cores
				//-------------------------------------------------------------------------------------------------------------------------

				//Remove transparent cores frpm previous viz
				for (let i = 0; i < extrCoreMeshes.length; i++) {
					scene.remove(extrCoreMeshes[i]);
				}

				const extrudeSettingsCore = {
					steps: 2,
					depth: this.buildingHeight,
					bevelEnabled: false,
					bevelThickness: 1,
					bevelSize: 1,
					bevelOffset: 0,
					bevelSegments: 1,
				};

				const extrCoreMaterial = new THREE.MeshLambertMaterial({ color: 0xe6e6e6, side: THREE.DoubleSide });
				extrCoreMaterial.opacity = 1;
				extrCoreMaterial.transparent = true;

				//Material
				var renderColor = 0xffffff;
				var renderOpacity = 0.9;

				const material = new THREE.MeshLambertMaterial({ color: renderColor, side: THREE.DoubleSide });
				material.opacity = renderOpacity;
				material.transparent = true;

				for (let i = 0; i < corePts.length; i++) {
					var extrCoreGeometry = new THREE.ExtrudeGeometry(new THREE.Shape(corePts[i]), extrudeSettingsCore);
					extrCoreMesh = new THREE.Mesh(extrCoreGeometry, extrCoreMaterial);
					extrCoreMeshes.push(extrCoreMesh);

					scene.add(extrCoreMesh);
				}

				//Slabs
				//-------------------------------------------------------------------------------------------------------------------------

				var slabGeometries = [];

				for (let i = 0; i < floorSlabs.length; i++) {
					// console.log("Floor slab plate height: ");
					var plateHeight = BuildingGeometry.getSlabHeight(floorSlabs[i]);

					var slabMesh = MemberViz.visualizeSlab(floorSlabs[i]);
					slabMesh.position.z = actualFloorHeight * (i + 1) - plateHeight / 2; //Minus slabHeight/2 !!!!!!!!
					scene.add(slabMesh);

					// var slabGeometry = MemberViz.visualizeSlab(floorSlabs[i]);
					// slabGeometry.translate.z = actualFloorHeight * (i + 1) - plateHeight/2;			//Minus slabHeight/2 !!!!!!!!
					// slabGeometries.push(slabGeometry);
				}

				// var mergedSlabGeometries = BufferGeometryUtils.mergeBufferGeometries(slabGeometries);
				// console.log(mergedSlabGeometries);

				// var mergedSlabMesh = new THREE.Mesh(mergedSlabGeometries);
				// console.log(mergedSlabMesh);

				// scene.add(mergedSlabMesh);

				//Foundation
				//-------------------------------------------------------------------------------------------------------------------------
				var groundSlabMesh = MemberViz.visualizeFoundation(groundSlab);
				scene.add(groundSlabMesh);

				//Columns
				//-------------------------------------------------------------------------------------------------------------------------

				var columnGeometries = [];

				for (let i = 0; i < columns.length; i++) {
					// var colMesh = MemberViz.visualizeColumn(columns[i], columns[i].shape);
					// scene.add(colMesh);

					var columnGeometry = MemberViz.visualizeColumn(columns[i], columns[i].shape);
					columnGeometries.push(columnGeometry);
				}

				var mergedColumnGeometries = BufferGeometryUtils.mergeBufferGeometries(columnGeometries);
				// console.log(mergedColumnGeometries);

				var mergedColumnMesh = new THREE.Mesh(mergedColumnGeometries);
				// console.log(mergedColumnMesh);

				scene.add(mergedColumnMesh);

				//Beams
				//-------------------------------------------------------------------------------------------------------------------------

				var beamGeometries = [];

				for (let i = 0; i < beams.length; i++) {
					// var beamMesh = MemberViz.visualizeBeam(beams[i], beams[i].shape, this.structSystem, floorSlabs[0], this.slabHeight);
					// scene.add(beamMesh);

					var beamGeometry = MemberViz.visualizeBeam(beams[i], beams[i].shape, this.structSystem, floorSlabs[0], this.slabHeight);
					beamGeometries.push(beamGeometry);
				}
				// console.log(beamGeometries);

				// //Edge Beams
				for (let i = 0; i < edgeBeamsTest.length; i++) {
					// var edgeBeamMesh = MemberViz.visualizeBeam(edgeBeamsTest[i], edgeBeamsTest[i].shape, this.structSystem, floorSlabs[0], this.slabHeight);
					// scene.add(edgeBeamMesh);

					var edgeBeamGeometry = MemberViz.visualizeBeam(edgeBeamsTest[i], edgeBeamsTest[i].shape, this.structSystem, floorSlabs[0], this.slabHeight);
					beamGeometries.push(edgeBeamGeometry);
				}

				var mergedBeamGeometries = BufferGeometryUtils.mergeBufferGeometries(beamGeometries);
				// console.log(mergedBeamGeometries);

				var mergedBeamMesh = new THREE.Mesh(mergedBeamGeometries);
				// console.log(mergedBeamMesh);

				scene.add(mergedBeamMesh);

				// this.createRoche1();

				//Pedestal
				//-------------------------------------------------------------------------------------------------------------------------
				// this.getPedestal(buildingPts);
			} else {
				alert("No calculated result to visualize!");
			}
		},
		//--------------------------------------------------------------------------------------------------------------
		//Drawing Tool
		//--------------------------------------------------------------------------------------------------------------

		addSphere(position) {
			const geometry = new THREE.SphereGeometry(0.3); //Radius, WidthSegments, HeightSegements
			const material = new THREE.MeshBasicMaterial({ color: 0xebebeb });
			material.opacity = 1;
			material.transparent = true;
			const object = new THREE.Mesh(geometry, material);

			if (position) {
				object.position.copy(position);
			}

			object.castShadow = true;
			object.receiveShadow = true;
			scene.add(object);
			lineHelperObjects.push(object);
			// return object;
		},
		onPointerMove(event) {
			const raycaster = new THREE.Raycaster();
			const pointer = new THREE.Vector2();

			pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
			pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

			raycaster.setFromCamera(pointer, camera);

			const intersects = raycaster.intersectObjects(lineHelperObjects, false);

			if (intersects.length > 0) {
				const object = intersects[0].object;
				if (object !== transformControl.object) {
					transformControl.attach(object);
				}
			}
		},
		onPointerDown(event) {
			onDownPosition.x = event.clientX;
			onDownPosition.y = event.clientY;
		},
		onPointerUp(event) {
			onUpPosition.x = event.clientX;
			onUpPosition.y = event.clientY;
			if (onDownPosition.distanceTo(onUpPosition) === 0) transformControl.detach();
		},
		createRoche() {
			const extrudeSettingsBuilding = {
				steps: 2,
				depth: 26.28,
				bevelEnabled: false,
				bevelThickness: 1,
				bevelSize: 1,
				bevelOffset: 0,
				bevelSegments: 1,
			};

			//Material
			const extrBuildingMaterial = new THREE.MeshLambertMaterial({ color: Color.MaterialColor.buildingMesh, side: THREE.DoubleSide });
			extrBuildingMaterial.opacity = 0.3;
			extrBuildingMaterial.transparent = true;
			extrBuildingMaterial.depthWrite = false;

			const extrBuildingMaterial1 = new THREE.MeshLambertMaterial({ color: 0xffffff, side: THREE.DoubleSide });
			extrBuildingMaterial1.opacity = 0.2;
			extrBuildingMaterial1.transparent = true;
			extrBuildingMaterial1.depthWrite = false;

			//Building1
			var building1Points = [new THREE.Vector3(-32, 13.3, 0), new THREE.Vector3(43.9, 13.3, 0), new THREE.Vector3(43.9, 34, 0), new THREE.Vector3(-32, 34, 0), new THREE.Vector3(-32, 13.3, 0)];
			var building1Shape = new THREE.Shape(building1Points);
			var building1Geometry = new THREE.ExtrudeGeometry(building1Shape, extrudeSettingsBuilding);
			var building1Mesh = new THREE.Mesh(building1Geometry, extrBuildingMaterial);

			//Building2
			var building2Points = [new THREE.Vector3(-32, -35, 0), new THREE.Vector3(43.9, -35, 0), new THREE.Vector3(43.9, -0.5, 0), new THREE.Vector3(-32, -0.5, 0), new THREE.Vector3(-32, -35, 0)];
			var building2Shape = new THREE.Shape(building2Points);
			var building2Geometry = new THREE.ExtrudeGeometry(building2Shape, extrudeSettingsBuilding);
			var building2Mesh = new THREE.Mesh(building2Geometry, extrBuildingMaterial1);

			//Bridge1
			var bridge1Points = [new THREE.Vector3(-7.85, -0.5, 0), new THREE.Vector3(-4.4, -0.5, 0), new THREE.Vector3(-4.4, 13.3, 0), new THREE.Vector3(-7.85, 13.3, 0), new THREE.Vector3(-7.85, 13.3, 0)];
			var bridge1Shape = new THREE.Shape(bridge1Points);
			var bridge1Geometry = new THREE.ExtrudeGeometry(bridge1Shape, extrudeSettingsBuilding);
			var bridge1Mesh = new THREE.Mesh(bridge1Geometry, extrBuildingMaterial1);

			//Bridge2
			var bridge2Points = [new THREE.Vector3(23.2, -0.5, 0), new THREE.Vector3(26.65, -0.5, 0), new THREE.Vector3(26.65, 13.3, 0), new THREE.Vector3(23.2, 13.3, 0), new THREE.Vector3(23.2, -0.5, 0)];
			var bridge2Shape = new THREE.Shape(bridge2Points);
			var bridge2Geometry = new THREE.ExtrudeGeometry(bridge2Shape, extrudeSettingsBuilding);
			var bridge2Mesh = new THREE.Mesh(bridge2Geometry, extrBuildingMaterial1);

			scene.add(building1Mesh, building2Mesh, bridge1Mesh, bridge2Mesh);
		},
		createRoche1() {
			const extrudeSettingsBuilding = {
				steps: 2,
				depth: 26.28,
				bevelEnabled: false,
				bevelThickness: 1,
				bevelSize: 1,
				bevelOffset: 0,
				bevelSegments: 1,
			};

			//Material
			const extrBuildingMaterial = new THREE.MeshLambertMaterial({ color: Color.MaterialColor.buildingMesh, side: THREE.DoubleSide });
			extrBuildingMaterial.opacity = 0.3;
			extrBuildingMaterial.transparent = true;
			extrBuildingMaterial.depthWrite = false;

			const extrBuildingMaterial1 = new THREE.MeshLambertMaterial({ color: 0xffffff, side: THREE.DoubleSide });
			extrBuildingMaterial1.opacity = 0.2;
			extrBuildingMaterial1.transparent = true;
			extrBuildingMaterial1.depthWrite = false;

			//Building1
			var building1Points = [new THREE.Vector3(-32, 13.3, 0), new THREE.Vector3(43.9, 13.3, 0), new THREE.Vector3(43.9, 34, 0), new THREE.Vector3(-32, 34, 0), new THREE.Vector3(-32, 13.3, 0)];
			var building1Shape = new THREE.Shape(building1Points);
			var building1Geometry = new THREE.ExtrudeGeometry(building1Shape, extrudeSettingsBuilding);
			var building1Mesh = new THREE.Mesh(building1Geometry, extrBuildingMaterial);

			//Building2
			var building2Points = [new THREE.Vector3(-32, -35, 0), new THREE.Vector3(43.9, -35, 0), new THREE.Vector3(43.9, -0.5, 0), new THREE.Vector3(-32, -0.5, 0), new THREE.Vector3(-32, -35, 0)];
			var building2Shape = new THREE.Shape(building2Points);
			var building2Geometry = new THREE.ExtrudeGeometry(building2Shape, extrudeSettingsBuilding);
			var building2Mesh = new THREE.Mesh(building2Geometry, extrBuildingMaterial1);

			//Bridge1
			var bridge1Points = [new THREE.Vector3(-7.85, -0.5, 0), new THREE.Vector3(-4.4, -0.5, 0), new THREE.Vector3(-4.4, 13.3, 0), new THREE.Vector3(-7.85, 13.3, 0), new THREE.Vector3(-7.85, 13.3, 0)];
			var bridge1Shape = new THREE.Shape(bridge1Points);
			var bridge1Geometry = new THREE.ExtrudeGeometry(bridge1Shape, extrudeSettingsBuilding);
			var bridge1Mesh = new THREE.Mesh(bridge1Geometry, extrBuildingMaterial1);

			//Bridge2
			var bridge2Points = [new THREE.Vector3(23.2, -0.5, 0), new THREE.Vector3(26.65, -0.5, 0), new THREE.Vector3(26.65, 13.3, 0), new THREE.Vector3(23.2, 13.3, 0), new THREE.Vector3(23.2, -0.5, 0)];
			var bridge2Shape = new THREE.Shape(bridge2Points);
			var bridge2Geometry = new THREE.ExtrudeGeometry(bridge2Shape, extrudeSettingsBuilding);
			var bridge2Mesh = new THREE.Mesh(bridge2Geometry, extrBuildingMaterial1);

			scene.add(building2Mesh, bridge1Mesh, bridge2Mesh);
		},
		catchExaggerateValues() {
			var warning = "No warning";

			//Steel

			if (this.material == "Steel") {
				if (this.xSpanValue > 12) {
					warning = "Span in X-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.ySpanValue > 12) {
					warning = "Span in Y-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.liveLoadValue > 5) {
					warning = "Live load value exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.deadLoadValue > 3) {
					warning = "Dead load value exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
			}

			//Concrete
			if (this.material == "Concrete") {
				if (this.xSpanValue > 9) {
					warning = "Span in X-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.ySpanValue > 9) {
					warning = "Span in Y-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.liveLoadValue > 5) {
					warning = "Live load value exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.deadLoadValue > 3) {
					warning = "Dead load value exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
			}

			//Timber
			if (this.material == "Timber") {
				if (this.structSystem == "Plate") {
					if (this.xSpanValue > 7) {
						warning = "Span in X-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
					if (this.ySpanValue > 7) {
						warning = "Span in Y-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
				}

				if (this.structSystem == "Beam") {
					if (this.xSpanValue > 10) {
						warning = "Span in X-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
					if (this.ySpanValue > 10) {
						warning = "Span in Y-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
				}

				if (this.liveLoadValue > 5) {
					warning = "Live load value exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
				if (this.deadLoadValue > 3) {
					warning = "Dead load value exceeded for material! \nPlease pay attention to the maximal slider value.";
				}
			}

			//Hybrid
			if (this.material == "Hybrid") {
				if (this.structSystem == "Plate") {
					if (this.xSpanValue > 12) {
						warning = "Span in X-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
					if (this.ySpanValue > 12) {
						warning = "Span in Y-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}

					if (this.liveLoadValue > 5) {
						warning = "Live load value exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
					if (this.deadLoadValue > 1.75) {
						warning = "Dead load value exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
				}

				if (this.structSystem == "Beam") {
					if (this.xSpanValue > 7) {
						warning = "Span in X-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
					if (this.ySpanValue > 7) {
						warning = "Span in Y-Direction exceeded for material! \nPlease pay attention to the maximal slider value.";
					}

					if (this.liveLoadValue > 5) {
						warning = "Live load value exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
					if (this.deadLoadValue > 3) {
						warning = "Dead load value exceeded for material! \nPlease pay attention to the maximal slider value.";
					}
				}
			}

			return warning;
		},
		// onMouseDown() {
		// },
		// onMouseUp() {
		// }
		clearAll() {
			console.log("Clear All");

			//Empty all member arrays
			buildingOutlines = [];
			gridlinesX = [];
			gridlinesY = [];
			intersectedGridlinesX = [];
			intersectedGridlinesY = [];
			intersectedSecondaryBeams = [];
			coreLines = [];
			xSegments = [];
			ySegments = [];
			secondaryBeamSegments = [];
			xBeamsPosition = [];
			xBeams = [];
			yBeamsPosition = [];
			yBeams = [];
			secondaryBeamsPosition = [];
			secondaryBeams = [];
			edgeBeams = [];
			beams = [];
			columns = [];
			innerColumnPositions = [];
			innerColumns = [];
			outerColumnPositions = [];
			outerColumns = [];
			edgeColumnPositions = [];
			edgeColumns = [];
			floorSlabs = [];
			groundSlab;

			//Reset result
			this.buildingResult.surfaceArea = 0;
			this.buildingResult.clearFloorHeight = 0;
			this.buildingResult.structCeilingHeight = 0;
			this.buildingResult.weight = 0;
			this.buildingResult.cost = 0;
			this.buildingResult.carbonData.total = 0;
			this.buildingResult.carbonData.slabs = 0;
			this.buildingResult.carbonData.columns = 0;
			this.buildingResult.carbonData.foundation = 0;
			this.buildingResult.carbonData.total = 0;
			this.buildingResult.carbonData.core = 0;
		},
		onContainerResize() {
			camera.aspect = container.clientWidth / container.clientHeight;
			camera.updateProjectionMatrix();
			renderer.setSize(container.clientWidth, container.clientHeight);
		},
		checkResults() {
			this.showResultModal = true;

			// this.showCommandLine = true;
			// this.commandLineText = "Please request full version."
		},
		closeModal() {
			this.showResultModal = false;
		},
		closeDashboard() {
			this.showDashboard = false;
		},
		makeGradientColor() {
			var c = document.getElementById("gradient-canvas");
			var ctx = c.getContext("2d");

			this.$store.state.ctx = ctx;

			// console.log(ctx);
		},
		getPedestal(buildingPts) {
			var x = 0;
			var y = 0;
			for (let i = 0; i < buildingPts.length; i++) {
				x = x + buildingPts[i].x;
				y = y + buildingPts[i].y;
			}
			const midPt = new THREE.Vector3(x / buildingPts.length, y / buildingPts.length, 0);

			var length = 0;

			for (let i = 0; i < buildingPts.length; i++) {
				var line = new THREE.Line3(midPt, buildingPts[i]);
				var iterationLength = line.distance();

				if (iterationLength > length) {
					length = iterationLength;
				}
			}

			const cylinderGeometry = new THREE.CylinderGeometry(length * 1.1, length * 1.1, 1, 80);
			const pedestalMaterial = new THREE.MeshLambertMaterial({ color: 0xa8a8a8 });
			const cylinderMesh = new THREE.Mesh(cylinderGeometry, pedestalMaterial);

			cylinderMesh.rotation.x = Math.PI / 2;
			cylinderMesh.position.x = midPt.x;
			cylinderMesh.position.y = midPt.y;
			cylinderMesh.position.z = -0.44;

			scene.add(cylinderMesh);
		},
		getDatabaseMaterial() {
			Firebase.getDataFromDb();
		},
		animate() {
			renderer.setAnimationLoop(() => {
				// controls.update();
				composer.render();
				renderer.render(scene, camera);
			});
		},
		render() {
			renderer.render(scene, camera);
		},
	},
	mounted() {
		this.init();
		this.animate();
		setTimeout(this.loadModel, 2000);
	},
	watch: {
		loading: {
			handler() {
				this.showLoader = true;
			},
		},
		"$store.state.columnLoadExceeded": {
			deep: true,
			handler() {
				// console.log('columnLoadExceeded from store');

				if (this.$store.state.columnLoadExceeded == true) {
					this.showCommandLine = true;
					this.commandLineText = "ERROR: Max. column load exceeded! Reduce load or change material.";
				}
			},
		},
	},
};
</script>

<style scoped>
#container {
	width: 100%;
	height: 100%;
	padding: 0px;
	margin: 0px;
	overflow: hidden;
	position: absolute;
}

#wrapper {
	width: 100%;
	height: 100%;
	margin: 0px;
	padding: 0px;
	overflow: hidden;
}

/* -------------------------------------------------------------------------------------------- */
/* Input Bar */
/* -------------------------------------------------------------------------------------------- */
#input-bar {
	/* position: fixed;
	vertical-align: middle;
	top: 14%;
	left: 0px; */
	position: fixed;
	vertical-align: middle;
	top: 11%;
	left: 0;
	/* left: -15.5%; */
}

/* -------------------------------------------------------------------------------------------- */
/* Result Bar */
/* -------------------------------------------------------------------------------------------- */
#result-bar {
	position: fixed;
	vertical-align: middle;
	top: 11%;
	right: 0;
	/* right: -19.5%; */
}

#value-test {
	position: fixed;
	vertical-align: middle;
	top: 10%;
	left: 90%;
}

/* -------------------------------------------------------------------------------------------- */
/* Drawing Tool */
/* -------------------------------------------------------------------------------------------- */
#container-draw {
	padding: 10px;
	height: 80px;
	max-width: 500px;
	top: 90%;
	left: 46%;
	/* border: 2px solid rgba(30, 30, 30, 0.2); */
	position: fixed;
	background: transparent;
	border-radius: 10px;
	display: block;
	justify-content: space-between;
	transform: translate(-50%, 0);
}

.container-buttons-draw {
	padding: 5px;
	height: 50px;
	width: 680px;
	position: fixed;
	/* background: #d4d4d4; */
	border-radius: 10px;
	/* border: 1px solid black; */
	/* box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);  */
	align-items: center;
	display: flex;
	justify-content: space-between;
	transform: translate(-50%, 0);
}

.button-draw-container {
	/* border: 1px solid black; */
	display: inline-block;
	margin-right: 8px;
}

.button-draw {
	padding: 5px;
	margin-right: 25px;
	border-radius: 20px;
	border: 0px solid rgba(107, 107, 107, 1);
	height: 34px;
	width: 64px;
	/* background: #f0f0f0; */
	background: rgba(0, 0, 0, 0.7);
	cursor: pointer;
}

.button-draw:last-child {
	margin-right: 0;
}

.button-draw:hover {
	/* border: 1px solid rgba(66, 66, 66, 1); */
	background: rgb(88, 88, 88, 1);
}

.button-draw:active {
	opacity: 0.8;
}

.icon {
	height: 20px;
}

.icon-option {
	height: 12px;
	margin-right: 5px;
}

.button-edit {
	padding: 5px;
	border-radius: 5px;
	border: 1px solid rgba(0, 0, 0, 1);
	height: 30px;
	width: 35px;
	background: white;
}

#button-ruler {
	padding: 5px;
	margin-left: 25px;
	border-radius: 8px;
	border: 0px solid rgba(107, 107, 107, 1);
	height: 34px;
	width: 125px;
	/* background: #f0f0f0; */
	background: rgba(0, 0, 0, 0.7);
	color: white;
	display: flex;
	position: relative;
}

#ruler-text {
	position: absolute;
	top: -10%;
	left: 40%;
}

.tooltip {
	margin-top: -34px;
	/* width: 350px; */
	position: absolute;
	/* transform: translateX(-50%); */
	background: rgb(56, 56, 56);
	color: #fff;
	/* font-family: Roboto; */
	font-family: "Roboto", sans-serif;
	font-weight: 400;
	font-size: 12px;
	white-space: nowrap;
	padding: 3px 15px;
	border-radius: 20px;
	transform: translateX(-50%);
	opacity: 0;
}

.tooltip::before {
	content: "";
	position: absolute;
	left: 50%;
	top: 100%;
	border: 7px solid;
	border-color: rgb(56, 56, 56) #0000 #0000 #0000;
	transform: translateX(-50%);
}

#tooltip-draw-building {
	margin-left: 31px;
}

#tooltip-draw-core {
	margin-left: 31px;
}

#tooltip-edit {
	margin-left: 31px;
}

#tooltip-delete {
	margin-left: 31px;
}

#tooltip-upload {
	margin-left: 30px;
}

/* -------------------------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------------------------- */
#geometry-selector {
	display: flex;
	align-items: center;
	justify-content: center;
	/* font-family: Roboto; */
	font-family: "Roboto", sans-serif;
	font-weight: 500;
	font-size: 14px;
	/* border: 1px solid black; */
	border-radius: 20px;
	height: 34px;
	margin-right: 30px;
	cursor: pointer;
	padding: 0px 12px;
	background: rgba(0, 0, 0, 0.7);
	color: rgba(255, 255, 255, 1);
}

#geometry-list {
	position: absolute;
	list-style-type: none;
	margin: 0;
	padding: 8px 8px;
	/* font-family: Roboto; */
	font-family: "Roboto", sans-serif;
	/* height: 60px; */
	/* display: flex; */
	font-weight: 400;
	font-size: 14px;
	margin-top: -140px;
	margin-left: 0px;
	/* border: 1px solid black; */
	border-radius: 10px;
	align-items: center;
	align-content: center;
	background: rgba(0, 0, 0, 0.7);
	color: rgba(255, 255, 255, 1);
}

.geometry-option {
	cursor: pointer;
	padding: 8px;
	border-radius: 5px;
}

#option-sketch-building {
	margin-bottom: 5px;
}

.geometry-option:hover,
#geometry-selector:hover,
#button-upload:hover {
	background: rgb(88, 88, 88, 1);
}

.fas {
	margin-left: 8px;
	margin-right: 4px;
}

#file-input {
	display: none;
}

#file-input-label {
	cursor: pointer;
}

#button-upload {
	padding: 11px 22px 6px 22px;
	margin-right: 145px;
	border-radius: 20px;
	border: 0px solid rgba(107, 107, 107, 1);
	height: 34px;
	width: 64px;
	background: rgba(0, 0, 0, 0.7);
	cursor: pointer;
}

#tell-me {
	top: 92%;
	left: 15%;
	position: fixed;
}

#gradient-canvas {
	top: 80%;
	left: -20%;
	position: fixed;
	border: 1px solid rgba(0, 0, 0, 1);
	height: 100px;
	width: 100px;
}

/* -------------------------------------------------------------------------------------------- */
/* Check Modal */
/* -------------------------------------------------------------------------------------------- */
#check-modal {
}

/* -------------------------------------------------------------------------------------------- */
/* Loading Animation */
/* -------------------------------------------------------------------------------------------- */
#wrapper-loader {
	top: 50%;
	left: 50%;
	position: fixed;
}

/* -------------------------------------------------------------------------------------------- */
/* Command-Line */
/* -------------------------------------------------------------------------------------------- */
#command-line {
	width: 28rem;
	height: 1.8rem;
	/* border: 1px solid rgba(0, 0, 0, 1);  */
	position: fixed;
	top: 11%;
	bottom: 0;
	left: 0;
	right: 0;
	margin: 0 auto;
	font-family: "Roboto", sans-serif;
	font-size: 0.8rem;
	font-weight: 100;
	text-align: center;
	line-height: 1.8rem;
	border-radius: 0.9rem;
	background: rgba(0, 0, 0, 0.7);
	color: rgba(255, 255, 255, 1);
	/* display: flex;
	justify-content: space-between; */
}

#icon-cross {
	height: 0.7rem;
	font-size: 0.1rem;
	position: absolute;
	margin-left: 12.6rem;
	margin-top: -1.25rem;
	cursor: pointer;
}
</style>
